import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Login from './Login';
import PageTitle from './PageTitle';
import InstallPrompt from './components/InstallPrompt';

// Clean hand-off screen while a lazy chunk loads; respects the board's theme
const RouteFallback = ({ label }) => {
  const dark = localStorage.getItem('carlineDarkMode') === 'true';
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: dark ? '#0F1117' : '#ECEFF3',
      color: dark ? '#9AA3B2' : '#55606E',
      fontSize: 16,
      fontWeight: 600
    }}>
      <CircularProgress size={40} sx={{ color: dark ? '#60A5FA' : '#2563EB' }} />
      {label}
    </div>
  );
};

// Lazy-load with one automatic reload: after a deploy, a device still holding
// the previous index.html requests chunk files that no longer exist and would
// hang on the loading screen forever; a single forced reload fetches the fresh
// index instead.
const lazyWithReload = (importFn) =>
  React.lazy(() =>
    importFn().then(module => {
      sessionStorage.removeItem('chunkReloadAttempted');
      return module;
    }).catch(err => {
      if (!sessionStorage.getItem('chunkReloadAttempted')) {
        sessionStorage.setItem('chunkReloadAttempted', '1');
        window.location.reload();
        return new Promise(() => {}); // hold suspense until the reload lands
      }
      throw err;
    })
  );

const CarlineDesktop = lazyWithReload(() => import('./CarlineDesktop'));
const CarlineMobile = lazyWithReload(() => import('./CarlineMobile'));

// Device detection hook
const useDeviceType = () => {
  const [isMobile, setIsMobile] = useState(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent.toLowerCase()
    ) || window.innerWidth < 768;
  });

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768 || 
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          navigator.userAgent.toLowerCase()
        );
      setIsMobile(mobile);
    };

    // Add resize listener with debouncing
    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  return isMobile;
};

// Smart route component that detects device and redirects
const CarlineRouter = () => {
  const isMobile = useDeviceType();
  
  // Get any state that was passed from login
  const location = window.location;
  const searchParams = new URLSearchParams(location.search);
  const urlParams = location.search; // Preserve all URL params
  
  // Redirect to appropriate route while preserving params
  if (isMobile) {
    return <Navigate to={`/carline/mobile${urlParams}`} replace />;
  } else {
    return <Navigate to={`/carline/desktop${urlParams}`} replace />;
  }
};

function App() {
  return (
    <Router>
      <PageTitle />
      <Routes>
        <Route path="/" element={<Login />} />
        
        {/* Main carline route - auto-detects and redirects */}
        <Route path="/carline" element={<CarlineRouter />} />
        
        {/* Specific routes for mobile and desktop */}
        <Route
          path="/carline/mobile"
          element={
            <React.Suspense fallback={<RouteFallback label="Opening carline…" />}>
              <CarlineMobile />
            </React.Suspense>
          }
        />

        <Route
          path="/carline/desktop"
          element={
            <React.Suspense fallback={<RouteFallback label="Opening your board…" />}>
              <CarlineDesktop />
            </React.Suspense>
          }
        />
      </Routes>
      <InstallPrompt />
    </Router>
  );
}

export default App;